import gql from 'graphql-tag';
import { prospectFragment } from './fragments';
import { useQuery } from 'react-apollo';
import { useContext } from 'react';
import { loggedContext } from '@food/auth/build/dist/utils/loggedContext';
import * as Sentry from '@sentry/browser';
import {
	Abbonamento,
	AbbonamentoEdge,
	Contact,
	PiListConnection,
	Prospect,
	User,
	UserAchievement,
} from '../server-types';
import { CampaignMember } from '@food/auth/build/dist/server-types';

const userMagazineDataQuery = gql`
	query UserMagazineDataQuery {
		currentUser {
			id
			localId
			firstName
			lastName
			langCode
			name
			email
			workEmail
			role
			status
			internalCapabilities

			userAchievements {
				id
				achievement
			}

			campaignMemberships {
				id
				CampaignId
				Esito_Campaign
			}

			Contact {
				id
				Items_Active_Access
			}

			Prospect {
				id
			}
		}
		abbonamenti(filter: { isActive: true, referenteIsCurrentUser: true }) {
			edges {
				node {
					id
					Fine
					Giorni_alla_scadenza
				}
			}
		}
	}
`;

export type UserMagazineDataType = Pick<
	User,
	| 'id'
	| 'localId'
	| 'firstName'
	| 'lastName'
	| 'langCode'
	| 'name'
	| 'email'
	| 'workEmail'
	| 'role'
	| 'status'
	| 'internalCapabilities'
> & {
	userAchievements: Pick<UserAchievement, 'id' | 'achievement'>[];
	campaignMemberships: Pick<CampaignMember, 'id' | 'CampaignId' | 'Esito_Campaign'>;
	Contact: Pick<Contact, 'id' | 'Items_Active_Access'>;
	Prospect: Pick<Prospect, 'id'>;
	abbonamenti: ReadonlyArray<Pick<Abbonamento, 'id' | 'Fine' | 'Giorni_alla_scadenza'>>;
};

export type useMagazineDataReturnType<T = any> = {
	loading: boolean;
	userMagazineData: T | null;
};

export const useUserMagazineData = (): useMagazineDataReturnType<UserMagazineDataType> => {
	const isLogged = !!useContext(loggedContext);

	const { loading, error, data } = useQuery(userMagazineDataQuery, { skip: !isLogged });

	if (error) {
		Sentry.captureException(error);
		console.error(error);
		return { loading, userMagazineData: null };
	}

	return {
		loading,
		userMagazineData: {
			...data?.currentUser,
			abbonamenti: data?.abbonamenti.edges.map((obj: AbbonamentoEdge) => obj.node),
		},
	};
};

export const userProfileDataQuery = gql`
	query UserProfileDataQuery {
		currentUser {
			id
			email
			workEmail

			Prospect {
				...ProspectFragment
			}
		}
		piLists(filter: { isCrmVisible: true, isPublic: true }) {
			edges {
				node {
					id
					piId
					name
					title
					description
					isPublic
					isDynamic
					isCrmVisible
				}
			}
		}
	}
	${prospectFragment}
`;

export type useProfileDataReturnType = {
	loading: boolean;
	userProfileData: Pick<User, 'email' | 'workEmail' | 'Prospect'> | null;
	piLists: PiListConnection | null;
};

export const useUserProfileData = (): useProfileDataReturnType => {
	const { loading, error, data } = useQuery(userProfileDataQuery);

	if (error) {
		Sentry.captureException(error);
		console.error(error);
		return { loading, userProfileData: null, piLists: null };
	}

	return { loading, userProfileData: data?.currentUser, piLists: data?.piLists };
};
